import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import {
  FaMoneyBillWave,
  FaPercent,
  FaCalendarAlt,
  FaEdit,
  FaCheckCircle,
  FaTimes,
  FaPaperPlane,
  FaHistory,
  FaSync,
  FaTrash,
  FaInfoCircle,
  FaExclamationTriangle,
  FaFilter,
  FaEye,
  FaReceipt,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExternalLinkAlt,
  FaSpinner,
  FaLock,
  FaDatabase,
  FaCalculator,
  FaLink,
  FaArrowRight,
  FaUsers,
  FaUserFriends,
  FaHome,
  FaUser,
  FaLayerGroup,
  FaRupeeSign,
} from "react-icons/fa";

import "./FamilyStudentExpense.css";

const FamilyStudentExpense = ({
  familyData,
  familyMembers,
  onRefresh,
  fullScreenMode = false,
}) => {
  // Expense Section State - FAMILY LEVEL (not student level)
  const [expenseRecords, setExpenseRecords] = useState([]);
  const [expenseData, setExpenseData] = useState({
    expensePayable: "0",
    expensePaid: "",
    expensePending: "0",
    discountType: "fixed",
    discountValue: "0",
    paidMonth: moment().format("MMMM"),
    expenseType: "other",
    description: "",
  });

  // Edit Form State
  const [editFormData, setEditFormData] = useState({
    expensePayable: "0",
    expensePaid: "0",
    discountType: "percentage",
    discountValue: "0",
    discountAmount: "0",
    discountedPayable: "0",
    expensePending: "0",
  });

  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isProcessingChainUpdate, setIsProcessingChainUpdate] = useState(false);

  // Filter and Sort State
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState(moment().year());
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal States
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Load expense data when family changes
  useEffect(() => {
    if (familyData?.familyCode) {
      fetchFamilyExpenseData();
    } else {
      resetExpenseForm();
    }
  }, [familyData?.familyCode]);

  // Update form balance when records change
  useEffect(() => {
    if (expenseRecords.length > 0 && familyData?.familyCode) {
      // Get the latest family expense record
      const latestRecord = getLatestFamilyRecord();
      
      if (latestRecord) {
        const latestBalance = parseFloat(latestRecord.expense_pending || 0);
        const currentBalance = parseFloat(expenseData.expensePayable) || 0;
        
        // Update if balance is different
        if (Math.abs(currentBalance - latestBalance) > 0.01) {
          setExpenseData((prev) => ({
            ...prev,
            expensePayable: latestBalance.toString(),
            expensePending: latestBalance.toString(),
          }));
        }
      }
    } else if (familyData?.familyCode) {
      // No records for this family, reset to 0
      setExpenseData((prev) => ({
        ...prev,
        expensePayable: "0",
        expensePending: "0",
      }));
    }
  }, [expenseRecords]);

  // Effect for edit form initialization
  useEffect(() => {
    if (editingExpense) {
      const payable = parseFloat(editingExpense.expense_payable) || 0;
      const paid = parseFloat(editingExpense.expense_paid) || 0;
      const discountType = editingExpense.discount_type || "percentage";
      const discountValue = parseFloat(editingExpense.discount_value) || 0;
      const discountAmount = parseFloat(editingExpense.discount_amount) || 0;
      const pending = parseFloat(editingExpense.expense_pending) || 0;

      // Calculate discounted payable
      const discountedPayable = Math.max(0, payable - discountAmount);

      setEditFormData({
        expensePayable: payable.toString(),
        expensePaid: paid.toString(),
        discountType: discountType,
        discountValue: discountValue.toString(),
        discountAmount: discountAmount.toString(),
        discountedPayable: discountedPayable.toString(),
        expensePending: pending.toString(),
      });
    }
  }, [editingExpense]);

  // Get the latest family expense record
  const getLatestFamilyRecord = () => {
    if (!expenseRecords.length) return null;
    
    // Sort by date descending to get the latest record
    const sortedRecords = [...expenseRecords].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    
    return sortedRecords[0];
  };

  // Get the base entry (very first record)
  const getBaseEntry = () => {
    if (!expenseRecords.length) return null;
    
    // Sort by date ascending to get the first record
    const sortedRecords = [...expenseRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
    
    return sortedRecords[0];
  };

  // Get latest family balance
  const getLatestFamilyBalance = () => {
    const latestRecord = getLatestFamilyRecord();
    if (!latestRecord) return 0;
    
    return parseFloat(latestRecord.expense_pending || 0);
  };

  // Reset expense form
  const resetExpenseForm = () => {
    setExpenseData({
      expensePayable: "0",
      expensePaid: "",
      expensePending: "0",
      discountType: "fixed",
      discountValue: "0",
      paidMonth: moment().format("MMMM"),
      expenseType: "other",
      description: "",
    });
    setExpenseRecords([]);
  };

  // Calculate discount amount for edit form
  const calculateEditDiscountAmount = (
    payable,
    discountType,
    discountValue
  ) => {
    const payableAmount = parseFloat(payable) || 0;
    const discountVal = parseFloat(discountValue) || 0;

    if (discountType === "percentage") {
      return (payableAmount * discountVal) / 100;
    } else {
      return Math.min(discountVal, payableAmount);
    }
  };

  // Calculate discounted payable for edit form
  const calculateEditDiscountedPayable = (
    payable,
    discountType,
    discountValue
  ) => {
    const payableAmount = parseFloat(payable) || 0;
    const discountAmount = calculateEditDiscountAmount(
      payable,
      discountType,
      discountValue
    );
    return payableAmount - discountAmount;
  };

  // Calculate pending amount for edit form
  const calculateEditPending = (payable, discountType, discountValue, paid) => {
    const payableAmount = parseFloat(payable) || 0;
    const paidAmount = parseFloat(paid) || 0;
    const discountedPayable = calculateEditDiscountedPayable(
      payable,
      discountType,
      discountValue
    );
    return Math.max(0, discountedPayable - paidAmount);
  };

  // Handle edit form field changes
  const handleEditFieldChange = (field, value) => {
    setEditFormData((prev) => {
      const updatedData = { ...prev, [field]: value };

      // Recalculate if relevant fields changed
      if (
        field === "expensePayable" ||
        field === "discountType" ||
        field === "discountValue" ||
        field === "expensePaid"
      ) {
        const payable =
          field === "expensePayable" ? value : prev.expensePayable;
        const discountType =
          field === "discountType" ? value : prev.discountType;
        const discountValue =
          field === "discountValue" ? value : prev.discountValue;
        const paid = field === "expensePaid" ? value : prev.expensePaid;

        // Calculate discount amount
        const discountAmount = calculateEditDiscountAmount(
          payable,
          discountType,
          discountValue
        );

        // Calculate discounted payable
        const discountedPayable = calculateEditDiscountedPayable(
          payable,
          discountType,
          discountValue
        );

        // Calculate pending
        const pending = calculateEditPending(
          payable,
          discountType,
          discountValue,
          paid
        );

        return {
          ...updatedData,
          discountAmount: discountAmount.toString(),
          discountedPayable: discountedPayable.toString(),
          expensePending: pending.toString(),
        };
      }

      return updatedData;
    });
  };

  // Calculate discounted amount
  const calculateDiscountedExpense = (
    payable = null,
    discountType = null,
    discountValue = null
  ) => {
    const payableAmount =
      payable !== null ? payable : parseFloat(expenseData.expensePayable) || 0;
    const discountTypeVal =
      discountType !== null ? discountType : expenseData.discountType;
    const discountValueVal =
      discountValue !== null
        ? discountValue
        : parseFloat(expenseData.discountValue) || 0;

    let discountAmount = 0;
    if (discountTypeVal === "percentage") {
      discountAmount = (payableAmount * discountValueVal) / 100;
    } else {
      discountAmount = discountValueVal;
    }

    discountAmount = Math.min(discountAmount, payableAmount);
    return payableAmount - discountAmount;
  };

  // Calculate discount amount
  const calculateExpenseDiscountAmount = (
    payable = null,
    discountType = null,
    discountValue = null
  ) => {
    const payableAmount =
      payable !== null ? payable : parseFloat(expenseData.expensePayable) || 0;
    const discountTypeVal =
      discountType !== null ? discountType : expenseData.discountType;
    const discountValueVal =
      discountValue !== null
        ? discountValue
        : parseFloat(expenseData.discountValue) || 0;

    if (discountTypeVal === "percentage") {
      return (payableAmount * discountValueVal) / 100;
    } else {
      return Math.min(discountValueVal, payableAmount);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === "" || amount === null || amount === undefined) {
      return "Rs. 0.00";
    }
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Handle expense paid change
  const handleExpensePaidChange = (value) => {
    const paidValue = parseFloat(value) || 0;
    const discountedPayable = calculateDiscountedExpense();

    let pendingAmount = discountedPayable - paidValue;
    pendingAmount = pendingAmount >= 0 ? pendingAmount : 0;

    setExpenseData((prev) => ({
      ...prev,
      expensePaid: value,
      expensePending: pendingAmount.toString(),
    }));
  };

  // Fetch family expense data
  const fetchFamilyExpenseData = async () => {
    if (!familyData?.familyCode) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=search-family-expense-details",
        {
          familyCode: familyData.familyCode,
        }
      );

      if (response.data && response.data.success) {
        let expenseDetails = [];

        // Handle different response formats
        if (Array.isArray(response.data.expenseDetails)) {
          expenseDetails = response.data.expenseDetails;
        } else if (
          response.data.expenseDetails &&
          typeof response.data.expenseDetails === "object"
        ) {
          expenseDetails = Object.values(response.data.expenseDetails);
        } else if (Array.isArray(response.data)) {
          expenseDetails = response.data;
        }

        // Sort by date
        const sortedDetails = expenseDetails.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setExpenseRecords(sortedDetails);
        
        // Update balance from latest record
        if (sortedDetails.length > 0) {
          const latestRecord = sortedDetails[0];
          const latestBalance = parseFloat(latestRecord.expense_pending || 0);
          
          setExpenseData((prev) => ({
            ...prev,
            expensePayable: latestBalance.toString(),
            expensePending: latestBalance.toString(),
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching family expense data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load family expense records. Please try again.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchFamilyExpenseData();
    if (onRefresh) onRefresh();
  };

  // Check if expense exists today for family
  const checkIfExpenseExistsToday = async () => {
    if (!familyData?.familyCode) return false;
    
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=check-today-expense",
        {
          family_code: familyData.familyCode,
          date: moment().format("YYYY-MM-DD"),
        }
      );
      return response.data.exists || false;
    } catch (error) {
      console.error("Error checking today's expense:", error);
      return false;
    }
  };

  // Handle Family Expense Submission (FAMILY LEVEL)
  const handleExpenseSubmit = async (event) => {
    event.preventDefault();

    const {
      expensePayable,
      expensePaid,
      discountType,
      discountValue,
      paidMonth,
      expenseType,
      description,
    } = expenseData;

    if (!familyData?.familyCode) {
      Swal.fire({
        title: "No Family Selected",
        text: "Please select a family first.",
        icon: "warning",
      });
      return;
    }

    const expensePayableValue = parseFloat(expensePayable) || 0;
    const expensePaidValue = parseFloat(expensePaid) || 0;
    const discountAmount = parseFloat(discountValue) || 0;

    // Calculate discount amount
    let calculatedDiscount = 0;
    if (discountType === "percentage") {
      calculatedDiscount = (expensePayableValue * discountAmount) / 100;
    } else {
      calculatedDiscount = discountAmount;
    }

    calculatedDiscount = Math.min(calculatedDiscount, expensePayableValue);
    const discountedPayable = expensePayableValue - calculatedDiscount;

    // Validations
    if (!expensePayableValue || expensePayableValue < 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid expense payable amount.",
        icon: "warning",
      });
      return;
    }

    if (!expensePaidValue || expensePaidValue <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid expense paid amount.",
        icon: "warning",
      });
      return;
    }

    if (expensePaidValue > discountedPayable) {
      Swal.fire({
        title: "Amount Exceeded",
        text: `Expense Paid cannot exceed ${formatCurrency(
          discountedPayable
        )}.`,
        icon: "error",
      });
      return;
    }

    const newPending = discountedPayable - expensePaidValue;

    const expenseSubmissionData = {
      family_code: familyData.familyCode,
      family_name: familyData.familyName,
      expense_payable: expensePayableValue,
      expense_paid: expensePaidValue,
      expense_pending: newPending,
      discount_type: discountType,
      discount_value: discountAmount,
      discount_amount: calculatedDiscount,
      paid_month: paidMonth,
      expense_type: expenseType,
      description: description || "",
    };

    setIsSubmitting(true);

    try {
      const duplicateCheck = await checkIfExpenseExistsToday();

      if (duplicateCheck) {
        const confirmResult = await Swal.fire({
          title: "Duplicate Transaction",
          html: `
            <div class="familyExpense-duplicate-warning">
              <FaExclamationTriangle style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
              <h3>Transaction Already Made Today</h3>
              <p>An expense transaction has already been recorded for today for ${familyData.familyName}.</p>
              <p><strong>Do you still want to make this payment?</strong></p>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, Submit Anyway",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (!confirmResult.isConfirmed) {
          setIsSubmitting(false);
          return;
        }
      }

      // Submit family expense
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=submit-family-expense",
        expenseSubmissionData
      );

      if (response.data.success) {
       Swal.fire({
  title: "✅ Success!",
  html: `
    <div style="
      text-align: center;
      padding: 2rem 1.5rem;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 16px;
    ">
      <!-- YOUR FULL HTML CONTENT (UNCHANGED) -->
      ${/* keep everything exactly as you already have */''}
    </div>
  `,
  icon: "success",
  iconHtml: '<div></div>',
  showCloseButton: true,
  closeButtonHtml: `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  `,
  confirmButtonText: "Done",
  confirmButtonColor: "#3085d6",

  customClass: {
    container: 'swal2-center',
    popup: 'swal2-popup-beautiful',
    title: 'swal2-title-beautiful',
    htmlf4htmlContainer: 'swal2-html-beautiful',
    closeButton: 'swal2-close-beautiful',
    confirmButton: 'swal2-confirm-beautiful'
  },

  background: '#ffffff',
  width: '520px',
  padding: '0',
  backdrop: 'rgba(0, 0, 0, 0.5)',
  position: 'center',

  /* 🔑 IMPORTANT */
  heightAuto: false,

  showClass: {
    popup: 'swal2-show-animation-beautiful'
  },
  hideClass: {
    popup: 'swal2-hide-animation-beautiful'
  }
});

        // Refresh data after successful submission
        fetchFamilyExpenseData();
        if (onRefresh) onRefresh();
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.error || "Failed to submit expense data.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Expense Submission Error:", error);
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.error ||
          "An error occurred while submitting expense data.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Edit Click
  const handleEditClick = (record) => {
    setEditingExpense(record);
    setShowEditModal(true);

    // Initialize edit form data
    const payable = parseFloat(record.expense_payable) || 0;
    const paid = parseFloat(record.expense_paid) || 0;
    const discountType = record.discount_type || "percentage";
    const discountValue = parseFloat(record.discount_value) || 0;
    const discountAmount = parseFloat(record.discount_amount) || 0;
    const pending = parseFloat(record.expense_pending) || 0;

    // Calculate discounted payable
    const discountedPayable = Math.max(0, payable - discountAmount);

    setEditFormData({
      expensePayable: payable.toString(),
      expensePaid: paid.toString(),
      discountType: discountType,
      discountValue: discountValue.toString(),
      discountAmount: discountAmount.toString(),
      discountedPayable: discountedPayable.toString(),
      expensePending: pending.toString(),
    });
  };

  // Handle Update Expense
  const handleUpdateExpense = async (updatedData) => {
    setIsProcessingChainUpdate(true);
    try {
      const payload = {
        id: updatedData.id,
        family_code: updatedData.family_code,
        family_name: updatedData.family_name,
        expense_payable: parseFloat(updatedData.expense_payable) || 0,
        expense_paid: parseFloat(updatedData.expense_paid) || 0,
        expense_pending: parseFloat(updatedData.expense_pending) || 0,
        discount_type: updatedData.discount_type || "percentage",
        discount_value: parseFloat(updatedData.discount_value) || 0,
        discount_amount: parseFloat(updatedData.discount_amount) || 0,
        paid_month: updatedData.paid_month,
        expense_type: updatedData.expense_type || "other",
        description: updatedData.description || "",
      };

      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=update-family-expense",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
     Swal.fire({
  title: "✅ Success!",
  html: `
    <div style="
      text-align: center;
      padding: 2rem 1.5rem;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 16px;
    ">
      <!-- Success Icon with Animation -->
      <div style="
        position: relative;
        display: inline-block;
        margin-bottom: 1.5rem;
      ">
        <div style="
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #27ae60, #2ecc71);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          box-shadow: 0 8px 20px rgba(39, 174, 96, 0.3);
          animation: successScale 0.6s ease-out;
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="white">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
        <div style="
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          border: 2px solid rgba(39, 174, 96, 0.2);
          border-radius: 50%;
          animation: ripple 1.5s infinite;
        "></div>
      </div>
      
      <!-- Success Message -->
      <div style="
        margin-bottom: 2rem;
      ">
        <h3 style="
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
          font-size: 1.6rem;
          font-weight: 700;
          line-height: 1.3;
        ">
          Update Successful!
        </h3>
        <p style="
          color: #6c757d;
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.5;
        ">
          Family expense record has been updated
        </p>
      </div>
      
      <!-- Receipt Card -->
      <div style="
        background: white;
        border-radius: 14px;
        padding: 1.5rem;
        margin: 0 auto 1.5rem auto;
        max-width: 420px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.08);
        border: 1px solid #e9ecef;
        text-align: left;
        position: relative;
        overflow: hidden;
      ">
        <!-- Corner Decoration -->
        <div style="
          position: absolute;
          top: 0;
          right: 0;
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #27ae60, transparent 70%);
          border-radius: 0 14px 0 0;
        "></div>
        
        <!-- Family Info -->
        <div style="
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f1f3f5;
        ">
          <div style="
            display: flex;
            align-items: center;
            margin-bottom: 0.75rem;
          ">
            <div style="
              width: 40px;
              height: 40px;
              background: linear-gradient(135deg, #6c5ce7, #a29bfe);
              border-radius: 10px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 0.75rem;
            ">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
            </div>
            <div>
              <div style="
                font-weight: 600;
                color: #2c3e50;
                font-size: 1.1rem;
                margin-bottom: 0.25rem;
              ">${updatedData.family_name}</div>
              <div style="
                display: flex;
                align-items: center;
                gap: 0.5rem;
              ">
                <span style="
                  background: #f1f3f5;
                  color: #495057;
                  padding: 0.25rem 0.75rem;
                  border-radius: 20px;
                  font-size: 0.8rem;
                  font-weight: 500;
                  font-family: 'SF Mono', monospace;
                  letter-spacing: 0.5px;
                ">${updatedData.family_code}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Amount Details -->
        <div>
          <div style="
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 1rem;
            font-size: 1rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          ">
            <div style="
              width: 6px;
              height: 20px;
              background: linear-gradient(to bottom, #3498db, #2980b9);
              border-radius: 3px;
            "></div>
            Updated Amount Details
          </div>
          
          <div style="
            background: #f8f9fa;
            border-radius: 10px;
            padding: 1rem;
            margin-bottom: 1rem;
          ">
            <!-- Payable -->
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.6rem 0;
            ">
              <div style="
                display: flex;
                align-items: center;
                gap: 0.5rem;
              ">
                <div style="
                  width: 20px;
                  height: 20px;
                  background: #e3f2fd;
                  border-radius: 6px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="#2196f3">
                    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                  </svg>
                </div>
                <span style="
                  font-weight: 500;
                  color: #495057;
                  font-size: 0.9rem;
                ">Payable</span>
              </div>
              <span style="
                font-weight: 700;
                color: #2c3e50;
                font-size: 1rem;
              ">${formatCurrency(updatedData.expense_payable)}</span>
            </div>
            
            <!-- Paid -->
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.6rem 0;
              border-top: 1px dashed #dee2e6;
              border-bottom: 1px dashed #dee2e6;
            ">
              <div style="
                display: flex;
                align-items: center;
                gap: 0.5rem;
              ">
                <div style="
                  width: 20px;
                  height: 20px;
                  background: #e8f5e9;
                  border-radius: 6px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="#4caf50">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <span style="
                  font-weight: 500;
                  color: #495057;
                  font-size: 0.9rem;
                ">Paid</span>
              </div>
              <span style="
                font-weight: 700;
                color: #27ae60;
                font-size: 1rem;
              ">${formatCurrency(updatedData.expense_paid)}</span>
            </div>
            
            <!-- Balance (Highlighted) -->
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.8rem;
              background: white;
              border-radius: 8px;
              margin-top: 0.8rem;
              border-left: 4px solid #3498db;
              box-shadow: 0 2px 8px rgba(52, 152, 219, 0.1);
            ">
              <div style="
                display: flex;
                align-items: center;
                gap: 0.5rem;
              ">
                <div style="
                  width: 24px;
                  height: 24px;
                  background: linear-gradient(135deg, #3498db, #2980b9);
                  border-radius: 6px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <span style="
                  font-weight: 700;
                  color: #2c3e50;
                  font-size: 1rem;
                ">New Balance</span>
              </div>
              <span style="
                font-weight: 800;
                color: #3498db;
                font-size: 1.1rem;
                letter-spacing: 0.5px;
              ">${formatCurrency(updatedData.expense_pending)}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Timestamp -->
      <div style="
        color: #adb5bd;
        font-size: 0.8rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
        </svg>
        Updated at ${moment().format('h:mm A')} • ${moment().format('DD MMM YYYY')}
      </div>
    </div>
  `,
  icon: "success",
  iconHtml: '<div></div>',
  showCloseButton: true,
  closeButtonHtml: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
  confirmButtonText: "Done",
  confirmButtonColor: "#3085d6",
  customClass: {
    container: 'swal2-center',
    popup: 'swal2-popup-beautiful',
    title: 'swal2-title-beautiful',
    htmlContainer: 'swal2-html-beautiful',
    closeButton: 'swal2-close-beautiful',
    confirmButton: 'swal2-confirm-beautiful'
  },
  background: '#ffffff',
  width: '520px',
  padding: '0',
  backdrop: 'rgba(0, 0, 0, 0.5)',
  position: 'center',
  showClass: {
    popup: 'swal2-show-animation-beautiful'
  },
  hideClass: {
    popup: 'swal2-hide-animation-beautiful'
  }
});

        fetchFamilyExpenseData();
        setShowEditModal(false);
        setEditingExpense(null);
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.error || "Failed to update expense.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.error || "Failed to update expense record.",
        icon: "error",
      });
    } finally {
      setIsProcessingChainUpdate(false);
    }
  };

  // Handle Edit Form Submission
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    // Prepare update data
    const updatedData = {
      id: editingExpense.id,
      family_code: editingExpense.family_code,
      family_name: editingExpense.family_name,
      expense_payable: parseFloat(editFormData.expensePayable) || 0,
      expense_paid: parseFloat(editFormData.expensePaid) || 0,
      expense_pending: parseFloat(editFormData.expensePending) || 0,
      discount_type: editFormData.discountType,
      discount_value: parseFloat(editFormData.discountValue) || 0,
      discount_amount: parseFloat(editFormData.discountAmount) || 0,
      paid_month: editingExpense.paid_month,
      expense_type: editingExpense.expense_type || "other",
      description: editingExpense.description || "",
    };

    await handleUpdateExpense(updatedData);
  };

  // Handle Delete Expense
  const handleDeleteExpense = async (recordId) => {
    // Check if it's the base entry
    const baseEntry = getBaseEntry();
    if (baseEntry && baseEntry.id === recordId) {
      Swal.fire({
        title: "Cannot Delete Base Entry",
        html: `
          <div class="familyExpense-baseEntry-warning">
            <FaLock style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
            <h3>Base Entry Cannot Be Deleted</h3>
            <p>This is the base expense entry for the family and cannot be deleted.</p>
            <p>You can update the amounts, but deletion is not allowed.</p>
          </div>
        `,
        icon: "warning",
        confirmButtonText: "Okay",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Delete Expense",
      html: `
        <div class="familyExpense-delete-confirm">
          <FaExclamationTriangle style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
          <h3>Delete Expense Record?</h3>
          <p>This will remove the expense record from the system.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete It",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (confirmResult.isConfirmed) {
      try {
        const response = await axios.post(
          "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=delete-family-expense",
          { id: recordId }
        );

        if (response.data.success) {
          Swal.fire({
            title: "Deleted!",
            text: "Family expense record has been deleted.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          fetchFamilyExpenseData();
          if (onRefresh) onRefresh();
        } else {
          Swal.fire({
            title: "Error",
            text: response.data.error || "Failed to delete expense record.",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete expense record.",
          icon: "error",
        });
      }
    }
  };

  // View Record Details
  const handleViewRecord = (record) => {
    setSelectedRecord(record);
  };

  // Filter and sort records
  const getFilteredRecords = () => {
    let filtered = [...expenseRecords];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (record) =>
          record.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          record.expense_type
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          record.paid_month?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply month filter
    if (filterMonth !== "all") {
      filtered = filtered.filter((record) => record.paid_month === filterMonth);
    }

    // Apply year filter
    if (filterYear !== "all") {
      filtered = filtered.filter(
        (record) => moment(record.created_at).year() === filterYear
      );
    }

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((record) => {
        const pending = parseFloat(record.expense_pending);
        if (filterStatus === "paid") return pending === 0;
        if (filterStatus === "pending") return pending > 0;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case "expense_payable":
        case "expense_paid":
        case "expense_pending":
          aValue = parseFloat(a[sortField]) || 0;
          bValue = parseFloat(b[sortField]) || 0;
          break;
        case "paid_month":
          const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          aValue = months.indexOf(a.paid_month);
          bValue = months.indexOf(b.paid_month);
          break;
        default:
          aValue = a[sortField] || "";
          bValue = b[sortField] || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Calculate totals
  const calculateTotals = () => {
    const records = getFilteredRecords();

    const totalPayable = records.reduce(
      (sum, r) => sum + parseFloat(r.expense_payable || 0),
      0
    );
    const totalPaid = records.reduce(
      (sum, r) => sum + parseFloat(r.expense_paid || 0),
      0
    );
    const totalPending = records.reduce(
      (sum, r) => sum + parseFloat(r.expense_pending || 0),
      0
    );

    const totalDiscount = records.reduce((sum, r) => {
      const discount = parseFloat(r.discount_amount || 0);
      if (discount > 0) return sum + discount;

      const payable = parseFloat(r.expense_payable || 0);
      const discountValue = parseFloat(r.discount_value || 0);
      if (r.discount_type === "percentage") {
        return sum + (payable * discountValue) / 100;
      }
      return sum + discountValue;
    }, 0);

    return {
      totalPayable,
      totalPaid,
      totalPending,
      totalDiscount,
      count: records.length,
    };
  };

  // If no family data is available
  if (!familyData?.familyCode) {
    return (
      <div className="familyExpense-emptyState">
        <div className="familyExpense-emptyStateIcon">
          <FaUsers />
        </div>
        <h3>No Family Selected</h3>
        <p>Please search for a family to manage expenses.</p>
      </div>
    );
  }

  return (
    <div className="familyExpense-container">
      {/* Loading Indicator */}
      {(isLoading || isProcessingChainUpdate) && (
        <div className="familyExpense-loading">
          <div className="familyExpense-loading-spinner">
            <FaSpinner className="spinner-icon" />
          </div>
          <p>
            {isProcessingChainUpdate
              ? "Updating expense..."
              : "Loading family expense data..."}
          </p>
        </div>
      )}

      {/* Family Info Banner */}
      <div className="familyExpense-familyInfo">
        <div className="familyExpense-familyInfo-content">
          <h3>{familyData.familyName} - Family Expense Management</h3>
          <div className="familyExpense-familyInfo-details">
            <span>Family Code: {familyData.familyCode}</span>
            <span>Members: {familyMembers?.length || 0}</span>
            <span>Total Records: {expenseRecords.length}</span>
          </div>
        </div>
        <button
          className="familyExpense-refreshBtn-main"
          onClick={handleRefresh}
        >
          <FaSync /> Refresh
        </button>
      </div>

      {/* Expense Form - FAMILY LEVEL */}
      <div className="familyExpense-form">
        <div className="familyExpense-formHeader">
          <FaMoneyBillWave className="familyExpense-formHeaderIcon" />
          <h3>Record Family Expense</h3>
          {expenseRecords.length > 0 && (
            <span className="familyExpense-recordCount">
              {expenseRecords.length} record
              {expenseRecords.length !== 1 ? "s" : ""} found
            </span>
          )}
        </div>

        <form onSubmit={handleExpenseSubmit}>
          <div className="familyExpense-formRow">
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaHome className="familyExpense-labelIcon" />
                Family Information
              </label>
              <div className="familyExpense-family-display">
                <div><strong>Family:</strong> {familyData.familyName}</div>
                <div><strong>Code:</strong> {familyData.familyCode}</div>
              </div>
            </div>

            {/* Family Balance Summary */}
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaLock className="familyExpense-labelIcon" />
                Family Balance Summary
              </label>
              <div className="familyExpense-family-summary">
                <div className="familyExpense-family-summary-item">
                  <span>Current Balance:</span>
                  <span>
                    {formatCurrency(getLatestFamilyBalance())}
                  </span>
                </div>
                <div className="familyExpense-family-summary-item">
                  <span>Last Payment:</span>
                  <span>
                    {(() => {
                      const latestRecord = getLatestFamilyRecord();
                      if (!latestRecord) return 'No payments yet';
                      return moment(latestRecord.created_at).format('DD-MM-YYYY');
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="familyExpense-formRow">
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                Next Payable (Calculated from last record)
              </label>
              <input
                type="text"
                className="familyExpense-formInput familyExpense-runningBalance"
                value={(() => {
                  const latestBalance = getLatestFamilyBalance();
                  return formatCurrency(latestBalance);
                })()}
                readOnly
              />
              <div className="familyExpense-balanceExplanation">
                {(() => {
                  const latestRecord = getLatestFamilyRecord();
                  const baseEntry = getBaseEntry();
                  
                  if (!latestRecord) {
                    return "No previous records. This will create the base entry.";
                  }
                  
                  const latestBalance = getLatestFamilyBalance();
                  const isBaseEntryLatest = baseEntry && baseEntry.id === latestRecord.id;
                  
                  return `Balance from ${moment(latestRecord.created_at).format('DD-MM-YYYY')}: ${formatCurrency(latestBalance)} ${
                    isBaseEntryLatest ? '(Base Entry)' : ''
                  }`;
                })()}
              </div>
            </div>

            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaPercent className="familyExpense-labelIcon" />
                Discount Type
              </label>
              <select
                className="familyExpense-formSelect"
                value={expenseData.discountType}
                onChange={(e) => {
                  const newType = e.target.value;
                  const payable = parseFloat(expenseData.expensePayable) || 0;
                  let newDiscountValue =
                    parseFloat(expenseData.discountValue) || 0;

                  if (
                    newType === "percentage" &&
                    expenseData.discountType === "fixed"
                  ) {
                    newDiscountValue =
                      payable > 0 ? (newDiscountValue / payable) * 100 : 0;
                  } else if (
                    newType === "fixed" &&
                    expenseData.discountType === "percentage"
                  ) {
                    newDiscountValue = (payable * newDiscountValue) / 100;
                  }

                  let discountAmount = 0;
                  if (newType === "percentage") {
                    discountAmount = (payable * newDiscountValue) / 100;
                  } else {
                    discountAmount = newDiscountValue;
                  }

                  const discountedPayable = payable - discountAmount;
                  const paidValue = parseFloat(expenseData.expensePaid) || 0;
                  const pendingAmount = discountedPayable - paidValue;

                  setExpenseData((prev) => ({
                    ...prev,
                    discountType: newType,
                    discountValue: newDiscountValue.toString(),
                    expensePending: (pendingAmount >= 0
                      ? pendingAmount
                      : 0
                    ).toString(),
                  }));
                }}
              >
                <option value="fixed">Fixed Amount (Rs.)</option>
                <option value="percentage">Percentage (%)</option>
              </select>
            </div>
          </div>

          <div className="familyExpense-formRow">
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                Discount{" "}
                {expenseData.discountType === "percentage" ? "(%)" : "(Rs.)"}
              </label>
              <input
                type="number"
                className="familyExpense-formInput"
                value={expenseData.discountValue}
                onChange={(e) => {
                  const discountValue = parseFloat(e.target.value) || 0;
                  const payable = parseFloat(expenseData.expensePayable) || 0;
                  const discountType = expenseData.discountType;

                  let discountAmount = 0;
                  if (discountType === "percentage") {
                    discountAmount = (payable * discountValue) / 100;
                  } else {
                    discountAmount = discountValue;
                  }

                  const discountedPayable = payable - discountAmount;
                  const paidValue = parseFloat(expenseData.expensePaid) || 0;
                  const pendingAmount = discountedPayable - paidValue;

                  setExpenseData((prev) => ({
                    ...prev,
                    discountValue: e.target.value,
                    expensePending: (pendingAmount >= 0
                      ? pendingAmount
                      : 0
                    ).toString(),
                  }));
                }}
                placeholder={
                  expenseData.discountType === "percentage" ? "0" : "0"
                }
                min="0"
                step="0.01"
                max={
                  expenseData.discountType === "percentage" ? "100" : undefined
                }
              />
            </div>

            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                Discount Amount
              </label>
              <input
                type="text"
                className="familyExpense-formInput familyExpense-discountedAmount"
                value={formatCurrency(calculateExpenseDiscountAmount())}
                readOnly
              />
            </div>
          </div>

          <div className="familyExpense-formRow">
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                Amount After Discount
              </label>
              <input
                type="text"
                className="familyExpense-formInput familyExpense-discountedAmount"
                value={formatCurrency(calculateDiscountedExpense())}
                readOnly
              />
            </div>

            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                Expense Paid
              </label>
              <input
                type="number"
                className="familyExpense-formInput"
                value={expenseData.expensePaid}
                onChange={(e) => handleExpensePaidChange(e.target.value)}
                placeholder="Enter amount"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          <div className="familyExpense-formRow">
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                New Balance (Next Payable)
              </label>
              <input
                type="text"
                className="familyExpense-formInput familyExpense-newBalance"
                value={formatCurrency(expenseData.expensePending)}
                readOnly
              />
              <div className="familyExpense-balanceExplanation">
                This will be the opening balance for the next family expense entry
              </div>
            </div>

            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaCalendarAlt className="familyExpense-labelIcon" />
                Paid Month
              </label>
              <select
                className="familyExpense-formSelect"
                value={expenseData.paidMonth}
                onChange={(e) =>
                  setExpenseData((prev) => ({
                    ...prev,
                    paidMonth: e.target.value,
                  }))
                }
                required
              >
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="familyExpense-formRow">
            <div className="familyExpense-formGroup">
              <label className="familyExpense-formLabel">
                <FaMoneyBillWave className="familyExpense-labelIcon" />
                Expense Type
              </label>
              <select
                className="familyExpense-formSelect"
                value={expenseData.expenseType}
                onChange={(e) =>
                  setExpenseData((prev) => ({
                    ...prev,
                    expenseType: e.target.value,
                  }))
                }
              >
                <option value="books">Books</option>
                <option value="uniform">Uniform</option>
                <option value="transport">Transport</option>
                <option value="exam">Exam Fee</option>
                <option value="activity">Activity</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="familyExpense-formGroup">
            <label className="familyExpense-formLabel">
              <FaEdit className="familyExpense-labelIcon" />
              Description (Optional)
            </label>
            <textarea
              className="familyExpense-formTextarea"
              value={expenseData.description}
              onChange={(e) =>
                setExpenseData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter expense description..."
              rows="2"
            />
          </div>

          <div className="familyExpense-formActions">
            <button
              type="submit"
              className="familyExpense-submitBtn"
              disabled={
                !familyData.familyCode ||
                !expenseData.expensePaid ||
                isSubmitting
              }
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner-icon" />
                  Processing...
                </>
              ) : (
                <>
                  <FaPaperPlane className="familyExpense-submitIcon" />
                  Submit Family Expense
                </>
              )}
            </button>
            <button
              type="button"
              className="familyExpense-historyBtn"
              onClick={() => setShowExpenseModal(true)}
              disabled={expenseRecords.length === 0}
            >
              <FaHistory className="familyExpense-historyIcon" />
              View & Manage Records
            </button>
          </div>
        </form>
      </div>

      {/* Recent Family Expenses */}
      {expenseRecords.length > 0 && (
        <div className="familyExpense-recentExpenses">
          <div className="familyExpense-recentHeader">
            <FaHistory className="familyExpense-recentIcon" />
            <h3>Recent Family Expense Records</h3>
            <button
              className="familyExpense-refreshBtn"
              onClick={handleRefresh}
              title="Refresh"
            >
              <FaSync />
            </button>
          </div>

          <div className="familyExpense-expenseTable">
            <table>
              <thead>
                <tr>
                  <th>Family</th>
                  <th>Month</th>
                  <th>Type</th>
                  <th>Payable</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenseRecords
                  .slice()
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                  )
                  .slice(0, 5)
                  .map((record, index) => {
                    const isBaseEntry = getBaseEntry()?.id === record.id;
                    
                    return (
                      <tr 
                        key={index} 
                        className={isBaseEntry ? "familyExpense-baseEntryRow" : ""}
                      >
                        <td>
                          <div><strong>{record.family_name}</strong></div>
                          <small>Code: {record.family_code}</small>
                          {isBaseEntry && (
                            <div className="familyExpense-baseEntryBadge">
                              <FaLock /> Base Entry
                            </div>
                          )}
                        </td>
                        <td>{record.paid_month}</td>
                        <td>
                          <span
                            className={`familyExpense-expenseTypeTag ${
                              record.expense_type || "other"
                            }`}
                          >
                            {record.expense_type || "Other"}
                          </span>
                        </td>
                        <td>{formatCurrency(record.expense_payable)}</td>
                        <td>{formatCurrency(record.expense_paid)}</td>
                        <td>
                          <span
                            className={`familyExpense-balance ${
                              parseFloat(record.expense_pending) === 0
                                ? "zero"
                                : ""
                            }`}
                          >
                            {formatCurrency(record.expense_pending)}
                          </span>
                        </td>
                        <td>
                          {moment(record.created_at).format("DD-MM-YYYY")}
                          {isBaseEntry && <div className="familyExpense-baseEntryNote">Initial</div>}
                        </td>
                        <td>
                          <div className="familyExpense-actions">
                            <button
                              className="familyExpense-viewBtn"
                              onClick={() => handleViewRecord(record)}
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="familyExpense-editBtn"
                              onClick={() => handleEditClick(record)}
                              title="Edit Record"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="familyExpense-deleteBtn"
                              onClick={() => handleDeleteExpense(record.id)}
                              title="Delete Record"
                              disabled={isBaseEntry}
                              style={isBaseEntry ? { 
                                opacity: 0.5, 
                                cursor: 'not-allowed',
                                background: '#f5f5f5',
                                color: '#999'
                              } : {}}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {expenseRecords.length > 5 && (
            <div className="familyExpense-viewAll">
              <button onClick={() => setShowExpenseModal(true)}>
                View All Records ({expenseRecords.length} entries)
                <FaExternalLinkAlt style={{ marginLeft: "8px" }} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Expense Records Modal */}
      {showExpenseModal && (
        <div className="familyExpense-modal-overlay">
          <div className="familyExpense-modal">
            <div className="familyExpense-modal-header">
              <div className="familyExpense-modal-header-left">
                <FaUsers className="familyExpense-modal-icon" />
                <div>
                  <h3>Family Expense Records - {familyData.familyName}</h3>
                  <p>
                    Family Code: {familyData.familyCode}
                  </p>
                </div>
              </div>
              <button
                className="familyExpense-modal-close"
                onClick={() => setShowExpenseModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="familyExpense-modal-filters">
              <div className="familyExpense-search-box">
                <FaSearch className="familyExpense-search-icon" />
                <input
                  type="text"
                  placeholder="Search by description, type, or month..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="familyExpense-search-input"
                />
              </div>

              <div className="familyExpense-filter-controls">
                <div className="familyExpense-filter-group">
                  <label>Month</label>
                  <select
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                  >
                    <option value="all">All Months</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="familyExpense-filter-group">
                  <label>Year</label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                  >
                    <option value="all">All Years</option>
                    {[2022, 2023, 2024, 2025, 2026].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="familyExpense-filter-group">
                  <label>Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>                  
                    </select>
                </div>

                <button
                  className="familyExpense-filter-clear"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterMonth("all");
                    setFilterYear("all");
                    setFilterStatus("all");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="familyExpense-modal-summary">
              {(() => {
                const totals = calculateTotals();
                return (
                  <>
                    <div className="familyExpense-summary-card">
                      <span className="familyExpense-summary-label">
                        Total Records
                      </span>
                      <span className="familyExpense-summary-value">
                        {totals.count}
                      </span>
                    </div>
                    <div className="familyExpense-summary-card">
                      <span className="familyExpense-summary-label">
                        Total Payable
                      </span>
                      <span className="familyExpense-summary-value">
                        {formatCurrency(totals.totalPayable)}
                      </span>
                    </div>
                    <div className="familyExpense-summary-card">
                      <span className="familyExpense-summary-label">
                        Total Paid
                      </span>
                      <span className="familyExpense-summary-value">
                        {formatCurrency(totals.totalPaid)}
                      </span>
                    </div>
                    <div className="familyExpense-summary-card">
                      <span className="familyExpense-summary-label">
                        Total Discount
                      </span>
                      <span className="familyExpense-summary-value">
                        {formatCurrency(totals.totalDiscount)}
                      </span>
                    </div>
                    <div className="familyExpense-summary-card familyExpense-summary-highlight">
                      <span className="familyExpense-summary-label">
                        Current Balance
                      </span>
                      <span className="familyExpense-summary-value">
                        {formatCurrency(totals.totalPending)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="familyExpense-table-container">
              <table className="familyExpense-modal-table">
                <thead>
                  <tr>
                    {[
                      { key: "created_at", label: "Date", sortable: true },
                      { key: "paid_month", label: "Month", sortable: true },
                      { key: "expense_type", label: "Type", sortable: false },
                      {
                        key: "description",
                        label: "Description",
                        sortable: false,
                      },
                      {
                        key: "expense_payable",
                        label: "Payable",
                        sortable: true,
                      },
                      {
                        key: "discount_value",
                        label: "Discount",
                        sortable: false,
                      },
                      { key: "expense_paid", label: "Paid", sortable: true },
                      {
                        key: "expense_pending",
                        label: "Balance",
                        sortable: true,
                      },
                      { key: "status", label: "Status", sortable: false },
                      { key: "actions", label: "Actions", sortable: false },
                    ].map((col) => (
                      <th key={col.key}>
                        {col.sortable ? (
                          <button
                            className="familyExpense-sort-btn"
                            onClick={() => {
                              if (sortField === col.key) {
                                setSortOrder(
                                  sortOrder === "asc" ? "desc" : "asc"
                                );
                              } else {
                                setSortField(col.key);
                                setSortOrder("desc");
                              }
                            }}
                          >
                            {col.label}
                            {sortField === col.key &&
                              (sortOrder === "asc" ? (
                                <FaSortUp />
                              ) : (
                                <FaSortDown />
                              ))}
                            {sortField !== col.key && <FaSort />}
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filteredRecords = getFilteredRecords();
                    return filteredRecords.map((record, index) => {
                      const payable = parseFloat(record.expense_payable) || 0;
                      const discountType = record.discount_type || "percentage";
                      const discountValue = parseFloat(
                        record.discount_value || 0
                      );
                      const discountAmount = parseFloat(
                        record.discount_amount || 0
                      );

                      const discountDisplay =
                        discountType === "percentage"
                          ? `${discountValue}%`
                          : formatCurrency(discountValue);

                      const isBaseEntry = getBaseEntry()?.id === record.id;

                      return (
                        <tr 
                          key={record.id} 
                          className={isBaseEntry ? "familyExpense-baseEntryRow" : ""}
                        >
                          <td>
                            {moment(record.created_at).format("DD-MM-YYYY")}
                            {isBaseEntry && (
                              <div className="familyExpense-baseEntryBadge">
                                <FaLock /> Base
                              </div>
                            )}
                          </td>
                          <td>{record.paid_month}</td>
                          <td>
                            <span
                              className={`familyExpense-expenseTypeTag ${
                                record.expense_type || "other"
                              }`}
                            >
                              {record.expense_type || "Other"}
                            </span>
                          </td>
                          <td className="familyExpense-description-cell">
                            {record.description || "-"}
                            {isBaseEntry && (
                              <div className="familyExpense-baseEntryNote">
                                Initial expense balance
                              </div>
                            )}
                          </td>
                          <td>{formatCurrency(payable)}</td>
                          <td>{discountDisplay}</td>
                          <td>{formatCurrency(record.expense_paid)}</td>
                          <td>
                            <span
                              className={`familyExpense-balance ${
                                parseFloat(record.expense_pending) === 0
                                  ? "zero"
                                  : ""
                              }`}
                            >
                              {formatCurrency(record.expense_pending)}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`familyExpense-statusBadge ${
                                parseFloat(record.expense_pending) === 0
                                  ? "paid"
                                  : "pending"
                              }`}
                            >
                              {parseFloat(record.expense_pending) === 0
                                ? "Paid"
                                : "Pending"}
                            </span>
                          </td>
                          <td>
                            <div className="familyExpense-actions">
                              <button
                                className="familyExpense-viewBtn"
                                onClick={() => handleViewRecord(record)}
                                title="View Details"
                              >
                                <FaEye />
                              </button>
                              <button
                                className="familyExpense-editBtn"
                                onClick={() => handleEditClick(record)}
                                title="Edit Record"
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="familyExpense-deleteBtn"
                                onClick={() => handleDeleteExpense(record.id)}
                                title="Delete Record"
                                disabled={isBaseEntry}
                                style={isBaseEntry ? { 
                                  opacity: 0.5, 
                                  cursor: 'not-allowed',
                                  background: '#f5f5f5',
                                  color: '#999'
                                } : {}}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>

              {getFilteredRecords().length === 0 && (
                <div className="familyExpense-noRecords">
                  <FaInfoCircle className="familyExpense-noRecords-icon" />
                  <p>
                    No family expense records found for the selected filters.
                  </p>
                  <button
                    className="familyExpense-clearFiltersBtn"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterMonth("all");
                      setFilterYear("all");
                      setFilterStatus("all");
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>

            <div className="familyExpense-modal-footer">
              <div className="familyExpense-export-options">
                <button className="familyExpense-export-btn">
                  <FaReceipt style={{ marginRight: "8px" }} />
                  Export to Excel
                </button>
                <button className="familyExpense-print-btn">
                  Print Records
                </button>
              </div>
              <div className="familyExpense-record-count">
                Showing {getFilteredRecords().length} of {expenseRecords.length}{" "}
                records
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {showEditModal && editingExpense && (
        <div className="familyExpense-edit-modal-overlay">
          <div className="familyExpense-edit-modal">
            <div className="familyExpense-edit-modal-header">
              <div className="familyExpense-edit-modal-header-left">
                <FaEdit className="familyExpense-edit-modal-icon" />
                <div>
                  <h3>Edit Family Expense Record</h3>
                  <p className="familyExpense-edit-subtitle">
                    Family: {editingExpense.family_name} ({editingExpense.family_code})
                  </p>
                  {getBaseEntry()?.id === editingExpense.id && (
                    <div className="familyExpense-edit-baseEntryWarning">
                      <FaLock className="familyExpense-baseEntryWarningIcon" />
                      <span>This is the base entry. You can update it but not delete it.</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                className="familyExpense-edit-modal-close"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingExpense(null);
                  setEditFormData({
                    expensePayable: "0",
                    expensePaid: "0",
                    discountType: "percentage",
                    discountValue: "0",
                    discountAmount: "0",
                    discountedPayable: "0",
                    expensePending: "0",
                  });
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="familyExpense-edit-modal-content">
              <form onSubmit={handleEditFormSubmit}>
                <div className="familyExpense-edit-form-row">
                  <div className="familyExpense-edit-form-group">
                    <label>Family Name</label>
                    <input
                      type="text"
                      value={editingExpense.family_name}
                      readOnly
                      className="familyExpense-readonly-input"
                    />
                  </div>
                  <div className="familyExpense-edit-form-group">
                    <label>Family Code</label>
                    <input
                      type="text"
                      value={editingExpense.family_code}
                      readOnly
                      className="familyExpense-readonly-input"
                    />
                  </div>
                </div>

                <div className="familyExpense-edit-form-row">
                  <div className="familyExpense-edit-form-group">
                    <label>Expense Payable</label>
                    <input
                      type="number"
                      value={editFormData.expensePayable}
                      onChange={(e) =>
                        handleEditFieldChange("expensePayable", e.target.value)
                      }
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="familyExpense-edit-form-group">
                    <label>Expense Paid</label>
                    <input
                      type="number"
                      value={editFormData.expensePaid}
                      onChange={(e) =>
                        handleEditFieldChange("expensePaid", e.target.value)
                      }
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="familyExpense-edit-form-row">
                  <div className="familyExpense-edit-form-group">
                    <label>Discount Type</label>
                    <select
                      value={editFormData.discountType}
                      onChange={(e) =>
                        handleEditFieldChange("discountType", e.target.value)
                      }
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (Rs.)</option>
                    </select>
                  </div>
                  <div className="familyExpense-edit-form-group">
                    <label>
                      Discount Value{" "}
                      {editFormData.discountType === "percentage"
                        ? "(%)"
                        : "(Rs.)"}
                    </label>
                    <input
                      type="number"
                      value={editFormData.discountValue}
                      onChange={(e) =>
                        handleEditFieldChange("discountValue", e.target.value)
                      }
                      step="0.01"
                      min="0"
                      max={
                        editFormData.discountType === "percentage"
                          ? "100"
                          : undefined
                      }
                      required
                    />
                  </div>
                </div>

                <div className="familyExpense-edit-form-row">
                  <div className="familyExpense-edit-form-group">
                    <label>Discount Amount</label>
                    <input
                      type="text"
                      value={formatCurrency(editFormData.discountAmount)}
                      readOnly
                      className="familyExpense-readonly-input"
                    />
                  </div>
                  <div className="familyExpense-edit-form-group">
                    <label>Amount After Discount</label>
                    <input
                      type="text"
                      value={formatCurrency(editFormData.discountedPayable)}
                      readOnly
                      className="familyExpense-readonly-input"
                    />
                  </div>
                </div>

                <div className="familyExpense-edit-form-row">
                  <div className="familyExpense-edit-form-group">
                    <label>Pending Balance</label>
                    <input
                      type="text"
                      value={formatCurrency(editFormData.expensePending)}
                      readOnly
                      className="familyExpense-readonly-input familyExpense-pending-highlight"
                    />
                  </div>
                  <div className="familyExpense-edit-form-group">
                    <label>Month</label>
                    <select
                      value={editingExpense.paid_month}
                      onChange={(e) => {
                        setEditingExpense((prev) => ({
                          ...prev,
                          paid_month: e.target.value,
                        }));
                      }}
                    >
                      {[
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ].map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="familyExpense-edit-form-row">
                  <div className="familyExpense-edit-form-group">
                    <label>Expense Type</label>
                    <select
                      value={editingExpense.expense_type || "other"}
                      onChange={(e) => {
                        setEditingExpense((prev) => ({
                          ...prev,
                          expense_type: e.target.value,
                        }));
                      }}
                    >
                      <option value="books">Books</option>
                      <option value="uniform">Uniform</option>
                      <option value="transport">Transport</option>
                      <option value="exam">Exam Fee</option>
                      <option value="activity">Activity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="familyExpense-edit-form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={editingExpense.description || ""}
                    onChange={(e) => {
                      setEditingExpense((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                    }}
                    rows="3"
                    placeholder="Enter expense description..."
                  />
                </div>

                <div className="familyExpense-edit-amount-summary">
                  <h4>Amount Summary</h4>
                  <div className="familyExpense-edit-summary-row">
                    <span>Payable:</span>
                    <span>{formatCurrency(editFormData.expensePayable)}</span>
                  </div>
                  <div className="familyExpense-edit-summary-row">
                    <span>
                      Discount (
                      {editFormData.discountType === "percentage"
                        ? `${editFormData.discountValue}%`
                        : "Fixed"}
                      ):
                    </span>
                    <span>{formatCurrency(editFormData.discountAmount)}</span>
                  </div>
                  <div className="familyExpense-edit-summary-row">
                    <span>After Discount:</span>
                    <span>
                      {formatCurrency(editFormData.discountedPayable)}
                    </span>
                  </div>
                  <div className="familyExpense-edit-summary-row">
                    <span>Paid:</span>
                    <span>{formatCurrency(editFormData.expensePaid)}</span>
                  </div>
                  <div className="familyExpense-edit-summary-row familyExpense-edit-summary-total">
                    <span>New Balance:</span>
                    <span>{formatCurrency(editFormData.expensePending)}</span>
                  </div>
                </div>

                <div className="familyExpense-edit-modal-actions">
                  <button
                    type="button"
                    className="familyExpense-edit-cancel"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingExpense(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="familyExpense-edit-save"
                    disabled={isProcessingChainUpdate}
                  >
                    {isProcessingChainUpdate ? (
                      <>
                        <FaSpinner className="spinner-icon" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Record Details Modal */}
      {selectedRecord && (
        <div className="familyExpense-view-modal-overlay">
          <div className="familyExpense-view-modal">
            <div className="familyExpense-view-modal-header">
              <div className="familyExpense-view-modal-header-left">
                <FaEye className="familyExpense-view-modal-icon" />
                <div>
                  <h3>Expense Record Details</h3>
                  <p>Family: {selectedRecord.family_name} ({selectedRecord.family_code})</p>
                  {getBaseEntry()?.id === selectedRecord.id && (
                    <div className="familyExpense-view-baseEntryInfo">
                      <FaLock /> 
                      <strong>Base Entry:</strong> This is the initial expense record that cannot be deleted.
                    </div>
                  )}
                </div>
              </div>
              <button
                className="familyExpense-view-modal-close"
                onClick={() => setSelectedRecord(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="familyExpense-view-modal-content">
              <div className="familyExpense-view-details-grid">
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Family Name:</span>
                  <span className="familyExpense-view-detail-value">{selectedRecord.family_name}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Family Code:</span>
                  <span className="familyExpense-view-detail-value">{selectedRecord.family_code}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Payable Amount:</span>
                  <span className="familyExpense-view-detail-value">{formatCurrency(selectedRecord.expense_payable)}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Amount Paid:</span>
                  <span className="familyExpense-view-detail-value">{formatCurrency(selectedRecord.expense_paid)}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Balance:</span>
                  <span className={`familyExpense-view-detail-value ${
                    parseFloat(selectedRecord.expense_pending) === 0 ? 'zero-balance' : 'pending-balance'
                  }`}>
                    {formatCurrency(selectedRecord.expense_pending)}
                  </span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Month:</span>
                  <span className="familyExpense-view-detail-value">{selectedRecord.paid_month}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Expense Type:</span>
                  <span className="familyExpense-view-detail-value">{selectedRecord.expense_type || 'Other'}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Discount Type:</span>
                  <span className="familyExpense-view-detail-value">{selectedRecord.discount_type || 'fixed'}</span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Discount Value:</span>
                  <span className="familyExpense-view-detail-value">
                    {selectedRecord.discount_type === 'percentage' 
                      ? `${selectedRecord.discount_value}%`
                      : formatCurrency(selectedRecord.discount_value)}
                  </span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Discount Amount:</span>
                  <span className="familyExpense-view-detail-value">{formatCurrency(selectedRecord.discount_amount)}</span>
                </div>
                {selectedRecord.description && (
                  <div className="familyExpense-view-detail-item full-width">
                    <span className="familyExpense-view-detail-label">Description:</span>
                    <span className="familyExpense-view-detail-value">{selectedRecord.description}</span>
                  </div>
                )}
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Created Date:</span>
                  <span className="familyExpense-view-detail-value">
                    {moment(selectedRecord.created_at).format('DD-MM-YYYY hh:mm A')}
                  </span>
                </div>
                <div className="familyExpense-view-detail-item">
                  <span className="familyExpense-view-detail-label">Last Updated:</span>
                  <span className="familyExpense-view-detail-value">
                    {selectedRecord.updated_at 
                      ? moment(selectedRecord.updated_at).format('DD-MM-YYYY hh:mm A')
                      : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            <div className="familyExpense-view-modal-footer">
              <button
                className="familyExpense-view-close-btn"
                onClick={() => setSelectedRecord(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyStudentExpense;